import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC
} from 'react'
import { useMediaQuery } from '~/hooks'
import { updateUrl } from '~/lib'
import { BusinessDetail } from '../BusinessDetail/BusinessDetail'
import { BusinessListing } from '../BusinessListing/BusinessListing'
import { FloatingAdvert } from '../FloatingAdvert/FloatingAdvert'
import { useFloatingAdvert } from '../FloatingAdvert/useFloatingAdvert'
import { Footer } from '../Footer/Footer'
import { type MapProps } from './Map.interface'
import { ResetButton } from './MapComponents/buttons'
import { usePreloadBusinessImages } from './usePreloadBusinessImages'
import styles from './Map.module.scss'

const Mapbox = dynamic(
  async () => await import('./Mapbox').then((mod) => mod.Mapbox),
  { ssr: false }
)

export const Map: FC<MapProps> = ({
  filters,
  businessListings,
  activeBusinessId,
  activeBusinessName,
  footer,
  businessPage,
  advertProps,
  ...mapClientProps
}) => {
  const businessListingRef = useRef<HTMLDivElement>(null)
  const businessDetailRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { preloadOnce } = usePreloadBusinessImages()

  const router = useRouter()
  const [businessId, setBusinesId] = useState<string | null>(
    activeBusinessId ?? null
  )
  const [hoveredBusinessId, setHoveredBusinessId] = useState<string | null>(
    null
  )

  const handleHoverBusinessId = useCallback((id: string | null): void => {
    setHoveredBusinessId(id)

    // Pre load the business images on hover as they are client, and only request image on click
    const business = businessListings.find((b) => b.id === id)
    if (business) {
      const [firstImage, secondImage, thirdImage] = business.images

      if (firstImage) {
        preloadOnce(firstImage.src, 400, 288, firstImage.alt)
      }
      if (secondImage) {
        preloadOnce(secondImage.src, 197, 141, secondImage.alt)
      }
      if (thirdImage) {
        preloadOnce(thirdImage.src, 197, 141, thirdImage.alt)
      }
    }
  }, [businessListings, preloadOnce])

  const [mapView, setMapView] = useState(false)

  const selectedBusiness = businessListings.find(
    (item) => item.id === businessId
  )

  const { showAdvert, handleCloseAdvert } = useFloatingAdvert()

  const [isMobile, setIsMobile] = useState(false)

  const mobileView = useMediaQuery('(max-width: 1024px)')
  const desktopView = useMediaQuery('(min-width: 1024px)')

  const [containerVariants, setContainerVariants] = useState({
    initial: {},
    animate: {},
    exit: {}
  })

  useEffect(() => {
    setIsMobile(mobileView)
    if (mobileView) {
      setContainerVariants({
        initial: {},
        animate: {},
        exit: {}
      })
    }
  }, [mobileView])

  useEffect(() => {
    setIsMobile(!desktopView)
    if (desktopView) {
      setContainerVariants({
        initial: {
          width: '100%',
          x: '100%',
          opacity: 0
        },
        animate: {
          x: selectedBusiness ? 0 : '100%',
          opacity: 1,
          transition: {
            ease: 'linear'
          }
        },
        exit: {
          x: '100%',
          opacity: 0,
          transition: {
            ease: 'linear'
          }
        }
      })
    }
  }, [desktopView, selectedBusiness])

  const handleSetId = useCallback(
    (bid: string | null): void => {
      setHoveredBusinessId(null)
      setBusinesId(null)

      if (bid) {
        setBusinesId(bid)
        const { name, id, categories } = router.query
        const business = businessListings.find((b) => b.id === bid)

        if (business) {
          containerRef?.current?.scrollTo({
            top: 0
          })

          updateUrl(window.location.href, {
            name: business.slug,
            id: bid
          })
        } else if (name ?? id) {
          updateUrl(window.location.href, {
            name,
            id,
            categories
          })
        }
      } else {
        updateUrl(window.location.href, {
          name: '',
          id: ''
        })
        setBusinesId(null)
      }
    },
    [businessListings, router]
  )
  return (
    <div ref={containerRef} className={styles.container}>
      {filters && (
        <div className={styles['business-listing-container']}>
          {!(selectedBusiness !== undefined && isMobile) && (
            <div style={{ height: '100%' }}>
              <BusinessListing
                businesses={businessListings}
                filters={filters}
                setBusinessId={handleSetId}
                setHoveredBusinessId={handleHoverBusinessId}
                setListView={setMapView}
                activeBusinessId={businessId}
                hoveredBusinessId={hoveredBusinessId}
                listView={mapView}
                footer={footer ?? null}
                containerRef={businessListingRef}
              />
            </div>
          )}
        </div>
      )}
      <AnimatePresence>
        {/* {selectedBusiness && ( */}
        <motion.div
          variants={containerVariants}
          className={classNames(styles['business-container'], {
            [styles['business-container--fixed']]: !businessPage
          })}
          initial={containerVariants.initial}
          animate={containerVariants.animate}
          exit={containerVariants.exit}
          ref={businessDetailRef}
        >
          {selectedBusiness && (
            <>
              {!businessPage && <ResetButton setBusinessId={handleSetId} />}
              <BusinessDetail
                businessPage={businessPage}
                footer={!isMobile ? footer : null}
              // eslint-disable-next-line react/jsx-props-no-spreading
                {...selectedBusiness}
              />
            </>
          )}
        </motion.div>
        {/* )} */}
      </AnimatePresence>
      {(!isMobile || mapView || selectedBusiness) && (
        <div
          className={classNames(
            styles['map-container'],
            mapView || businessId ? styles['map-view'] : '',
            businessId ? styles['detail-map-view'] : ''
          )}
          style={{ position: 'relative' }}
        >
          <Mapbox
            markers={mapClientProps.markers}
            setActiveId={handleSetId}
            activeId={businessId}
            hoveredId={hoveredBusinessId}
            isMobile={isMobile}
            businessPage={!!businessPage}
            businessDetailRef={businessDetailRef}
            onHover={handleHoverBusinessId}
          />

            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          {
          advertProps && (
          <FloatingAdvert
          // eslint-disable-next-line react/jsx-props-no-spreading
            {...advertProps}
            handleCloseAdvert={handleCloseAdvert}
            showAdvert={showAdvert}
          />
          )
         }

        </div>
      )}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      {isMobile && selectedBusiness && footer && <Footer {...footer} />}
    </div>
  )
}
